import React, { useEffect, useState } from 'react';
import LoginInput from '../components/auth/LoginInput';
import LoginSubmitButton from '../components/common/SubmitButton';
import { encryptData } from '../utils/security';
import { patch, post } from '../utils/fetch';
import {
  ChallengeNameType,
  ForgotPasswordResponse,
  InitiateAuthResponse,
} from '@aws-sdk/client-cognito-identity-provider';
import { storeToken } from '../utils/cookie';
import { useNavigate } from 'react-router';
import { validateEmail, validatePassword } from '../utils/validation';
import { useTranslation } from 'react-i18next';
import SVGImage from '../components/layout/SVGImage';
import { useLogger } from '../contexts/LoggerContext';
import { isEnumValue } from '../utils/util';
import { showToast } from '../slices/toastSlice';
import { useAppDispatch } from '../hooks/useRedux';
import { GridusException } from '../exceptions/gridusException';
import { PowerOutageScheduleFilterCheckbox } from '@/components/plant-overview/PowerOutageScheduleFilterCheckbox';

export enum LoginProgress {
  Login,
  ChangePassword,
  FindPassword,
  FindPasswordCodeSent,
  FindEmail,
  FindEmailResult,
}

function LoginPage() {
  const { log } = useLogger();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loginProgress, setLoginProgress] = useState(LoginProgress.Login);
  const [onSubmit, setOnSubmit] = useState(false);
  const dispatch = useAppDispatch();
  const [isRememberId, setIsRememberId] = useState(false);
  const [loginValue, setLoginValue] = useState({
    email: '',
    password: '',
    emailError: '',
    passwordError: '',
  });
  const [changePasswordValue, setChangePasswordValue] = useState({
    session: '',
    email: '',
    password: '',
    passwordGuidance: t('login.passwordGuidance'),
    passwordSuccess: '',
    passwordConfirm: '',
    passwordError: '',
    passwordConfirmError: '',
    passwordConfirmSuccess: '',
  });
  const [findPasswordValue, setFindPasswordValue] = useState({
    email: '',
    emailError: '',
    confirmCode: '',
    confirmCodeError: '',
    password: '',
    passwordGuidance: t('login.passwordGuidance'),
    passwordSuccess: '',
    passwordError: '',
    passwordConfirm: '',
    passwordConfirmError: '',
    passwordConfirmSuccess: '',
  });

  const [findEmailValue, setFindEmailValue] = useState({
    username: '',
    usernameError: '',
    contact: '',
    contactError: '',
    emailResult: '',
  });

  const isLoginSubmitButtonDisabled = () =>
    !loginValue.email || !loginValue.password || onSubmit;
  const isChangePasswordSubmitButtonDisabled = () =>
    !changePasswordValue.password ||
    !changePasswordValue.passwordConfirm ||
    onSubmit;
  const isFindPasswordSubmitButtonDisabled = () =>
    !findPasswordValue.email || onSubmit;
  const isFindEmailSubmitButtonDisabled = () =>
    !findEmailValue.contact || !findEmailValue.username || onSubmit;
  const isFindPasswordSentCodeSubmitButtonDisabled = () =>
    !findPasswordValue.password ||
    !findPasswordValue.passwordConfirm ||
    !findPasswordValue.confirmCode ||
    onSubmit;

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const progress = event.state?.progress;
      // because the state is already popped state.
      if (!progress || !isEnumValue(LoginProgress, progress))
        setLoginProgress(LoginProgress.Login);
      else setLoginProgress(progress);
    };

    window.addEventListener('popstate', handlePopState);

    // retrieveUserName
    const savedEmail = localStorage.getItem('savedUserEmail');
    if (savedEmail) {
      setLoginValue((prev) => ({ ...prev, email: savedEmail }));
      setIsRememberId(true);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const loginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation
    if (
      !validateEmail(loginValue.email, (value: string) =>
        setLoginValue((prev) => ({ ...prev, emailError: value }))
      )
    )
      return;
    if (
      !validatePassword(loginValue.password, (value: string) =>
        setLoginValue((prev) => ({ ...prev, passwordError: value }))
      )
    )
      return;

    // block login button
    setOnSubmit(true);

    // save email
    if (isRememberId) {
      localStorage.setItem('savedUserEmail', loginValue.email);
    } else {
      localStorage.removeItem('savedUserEmail');
    }

    try {
      // Encryption
      const userInfo = {
        password: loginValue.password,
        email: loginValue.email,
      };
      const requestBody = await encryptData(
        JSON.stringify(userInfo),
        'userInfo'
      );

      // Login Request
      const response = await post<{ data: InitiateAuthResponse }>(
        '/auth/login',
        {
          body: JSON.stringify(requestBody),
        }
      );

      // when response is error
      if (response.status === 'error') {
        switch (response.code) {
          // GridusUserNotFoundException
          // GridusNotAuthorizedException
          case 'V0002':
          case 'A0005':
            setLoginValue((prev) => ({
              ...prev,
              passwordError: t('login.login', { context: 'failed' }),
            }));
            setOnSubmit(false);
            return;
          default:
            throw new GridusException(response.message);
        }
      }

      // Store tokens
      const { ChallengeName, Session, AuthenticationResult } = response.data;

      if (AuthenticationResult) {
        const { AccessToken, IdToken, RefreshToken } = AuthenticationResult;

        if (!AccessToken || !IdToken || !RefreshToken)
          throw new GridusException(t('login.error.notAuthenticated'));

        // if authentication success set tokens in cookie
        storeAuthTokens(AccessToken, IdToken, RefreshToken);

        navigate('/forecast');
      } else if (ChallengeName === ChallengeNameType.NEW_PASSWORD_REQUIRED) {
        // 최초비밀번호 변경이 필요한 경우 비밀번호 변경 Progress로 이동
        if (!Session)
          throw new GridusException(t('login.error.sessionRequired'));
        changeLoginProgressToChangePassword(Session);
      }
    } catch (e) {
      navigate('/error', { state: { statusCode: '500' } });
      log.debug(e);
      setOnSubmit(false);
    }
  };

  const storeAuthTokens = (
    accessToken: string,
    idToken: string,
    refreshToken: string
  ) => {
    storeToken('accessToken', accessToken);
    storeToken('idToken', idToken);
    storeToken('refreshToken', refreshToken);
  };

  const pushHistory = (progress: LoginProgress) => {
    window.history.pushState({ progress }, '', '');
  };

  const changeLoginProgressToChangePassword = (session: string) => {
    setChangePasswordValue((prev) => ({
      ...prev,
      email: loginValue.email,
    }));
    setChangePasswordValue((prev) => ({ ...prev, session }));
    // reset onSubmit
    setOnSubmit(false);
    setLoginProgress(LoginProgress.ChangePassword);
    pushHistory(LoginProgress.ChangePassword);
  };

  const changeLoginProgressToFindPassword = () => {
    // reset onSubmit
    setOnSubmit(false);
    setLoginProgress(LoginProgress.FindPassword);
    pushHistory(LoginProgress.FindPassword);
  };

  const changeLoginProgressToFindEmail = () => {
    // reset onSubmit
    setOnSubmit(false);
    setLoginProgress(LoginProgress.FindEmail);
    pushHistory(LoginProgress.FindEmail);
  };

  const changePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation
    if (!validatePassword(changePasswordValue.password)) return;
    if (!validatePassword(changePasswordValue.passwordConfirm)) return;

    // block login button
    setOnSubmit(true);

    try {
      // Encryption
      const email = changePasswordValue.email;
      const password = changePasswordValue.password;
      const userInfo = {
        email,
        password,
      };

      const session = changePasswordValue.session;
      const requestBody = {
        ...(await encryptData(JSON.stringify(userInfo), 'userInfo')),
        session,
      };

      // Auth Request
      const response = await patch<{ data: InitiateAuthResponse }>(
        '/auth/user/password',
        {
          body: JSON.stringify(requestBody),
        }
      );

      // when response is error
      if (response.status === 'error') {
        throw new GridusException(response.message);
      }

      // Store tokens
      const {
        data: { AuthenticationResult },
      } = response;

      if (AuthenticationResult) {
        const { AccessToken, IdToken, RefreshToken } = AuthenticationResult;

        if (!AccessToken || !IdToken || !RefreshToken)
          throw new GridusException(t('login.error.notAuthenticated'));

        // if authentication success set tokens in cookie
        storeAuthTokens(AccessToken, IdToken, RefreshToken);

        navigate('/forecast');
      }
    } catch (e) {
      navigate('/error', { state: { statusCode: '500' } });
      log.debug(e);
      setOnSubmit(false);
    }
  };

  const codeSentSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation
    if (
      !validateEmail(findPasswordValue.email, (value: string) =>
        setFindPasswordValue((prev) => ({ ...prev, emailError: value }))
      )
    )
      return;
    if (!validatePassword(findPasswordValue.password)) return;
    if (!validatePassword(findPasswordValue.passwordConfirm)) return;
    if (!findPasswordValue.confirmCode) return;

    // Encryption
    const email = findPasswordValue.email;
    const password = findPasswordValue.password;
    const confirmationCode = findPasswordValue.confirmCode;
    const userInfo = {
      email,
      password,
      confirmationCode,
    };

    const requestBody = {
      ...(await encryptData(JSON.stringify(userInfo), 'userInfo')),
    };
    try {
      // block login button
      setOnSubmit(true);

      // Auth Request
      const response = await post('/auth/password-reset/confirm', {
        body: JSON.stringify(requestBody),
      });

      // when response is error
      if (response.status === 'error') {
        switch (response.code) {
          // GridusPasswordConfirmCodeMismatchException
          case 'V0003':
            setFindPasswordValue((prev) => ({
              ...prev,
              confirmCodeError: t('login.confirmCode', { context: 'failed' }),
            }));
            setOnSubmit(false);
            return;
          default:
            throw new GridusException(response.message);
        }
      }

      dispatch(showToast(t('login.findPassword', { context: 'success' })));
      navigate('/forecast');
    } catch (e) {
      navigate('/error', { state: { statusCode: '500' } });
      log.debug(e);
      setOnSubmit(false);
    }
  };

  const findPasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation
    if (
      !validateEmail(findPasswordValue.email, (value: string) =>
        setFindPasswordValue((prev) => ({ ...prev, emailError: value }))
      )
    )
      return;

    const email = findPasswordValue.email;
    const requestBody = {
      userInfo: {
        email,
      },
    };

    try {
      // block login button
      setOnSubmit(true);

      // Auth Request
      const response = await post<ForgotPasswordResponse>(
        '/auth/password-reset',
        {
          body: JSON.stringify(requestBody),
        }
      );

      // when response is error
      if (response.status === 'error') {
        throw new GridusException(response.message);
      }

      setLoginProgress(LoginProgress.FindPasswordCodeSent);
      setOnSubmit(false);
      pushHistory(LoginProgress.FindPasswordCodeSent);
    } catch (e) {
      navigate('/error', { state: { statusCode: '500' } });
      log.debug(e);
      setOnSubmit(false);
    }
  };

  const findEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const username = findEmailValue.username;
    const contact = findEmailValue.contact;
    const requestBody = {
      username,
      contact,
    };

    try {
      // block login button
      setOnSubmit(true);

      // Auth Request
      const response = await post<{ data: string }>('/auth/users/email', {
        body: JSON.stringify(requestBody),
      });

      // when response is error
      if (response.status === 'error') {
        throw new GridusException(response.message);
      }

      setFindEmailValue((prev) => ({ ...prev, emailResult: response.data }));
      setLoginProgress(LoginProgress.FindEmailResult);
      setOnSubmit(false);
      pushHistory(LoginProgress.FindEmailResult);
    } catch (e) {
      navigate('/error', { state: { statusCode: '500' } });
      log.debug(e);
      setOnSubmit(false);
    }
  };

  const findEmailResultSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginProgress(LoginProgress.Login);
    pushHistory(LoginProgress.Login);
  };

  return (
    <main className="flex h-screen w-full items-center justify-center bg-[linear-gradient(109deg,_#DACC80_-5.83%,_#74C496_50.62%,_#33B2CE_103.78%)] font-poppins">
      <div>
        <div className="mb-[53px] flex flex-col items-center justify-center text-[30px] font-bold">
          <div className="flex items-center">
            <SVGImage name="gridus-logo"></SVGImage>
            <SVGImage
              className="ml-[10px]"
              fill="#241F20"
              name="gridus-logo-text"
            ></SVGImage>
          </div>
          {loginProgress === LoginProgress.Login && (
            <>
              <p className="noto-14-light mt-[15px] block leading-[24px] text-dark-6">
                {t('brand.greeting', { context: 'first' })}
              </p>
              <p className="noto-15-medium block leading-[18px] tracking-[-0.5px] text-dark-6">
                {t('brand.greeting', { context: 'second' })}
              </p>
            </>
          )}
        </div>
        <div className="shadow-[0_13px_44px_rgba(0, 146, 179, 0.20)] w-[500px] rounded-[15px] bg-white p-[70px] opacity-85">
          {/* Login Progress */}
          {loginProgress === LoginProgress.Login && (
            <form noValidate onSubmit={loginSubmit}>
              <p
                data-testid="login-label"
                className="noto-bold-4 mb-[40px] leading-[24px] text-dark-4"
              >
                {t('login.login')}
              </p>
              <LoginInput
                id="email"
                name="email"
                type="email"
                testId="login-email-input"
                text={t('login.email')}
                required
                value={loginValue.email}
                setValue={(value) =>
                  setLoginValue((prev) => ({ ...prev, email: value }))
                }
                placeholder="example@gmail.com"
                className="noto-13-bold text-dark-10"
                error={loginValue.emailError}
              ></LoginInput>
              <div>
                <PowerOutageScheduleFilterCheckbox
                  isChecked={isRememberId}
                  onClick={() => setIsRememberId((prev) => !prev)}
                  text="아이디 저장"
                  className="pb-[20px] [&]:noto-13-normal"
                />
              </div>
              <LoginInput
                id="password"
                name="password"
                testId="login-password-input"
                type="password"
                value={loginValue.password}
                text={t('login.password')}
                className="noto-13-bold mb-[20px] text-dark-10"
                setValue={(value) =>
                  setLoginValue((prev) => ({ ...prev, password: value }))
                }
                required
                placeholder="password"
                error={loginValue.passwordError}
              ></LoginInput>
              <div
                className="flex cursor-pointer items-center justify-end"
                data-testid="find-password-button"
                onClick={changeLoginProgressToFindPassword}
              >
                <SVGImage width={20} height={20} name={'lock-icon'}></SVGImage>
                <p className="pop-13-semibold ml-[6px] leading-[24px] text-blue-light underline decoration-solid decoration-auto underline-offset-2px">
                  {t('login.findPassword')}
                </p>
              </div>
              <div
                className="flex cursor-pointer items-center justify-end"
                data-testid="find-email-button"
                onClick={changeLoginProgressToFindEmail}
              >
                <SVGImage width={20} height={20} name={'lock-icon'}></SVGImage>
                <p className="pop-13-semibold ml-[6px] leading-[24px] text-blue-light underline decoration-solid decoration-auto underline-offset-2px">
                  아이디 찾기
                </p>
              </div>
              <div className="mt-[38px] flex justify-end">
                <LoginSubmitButton
                  text={t('login.login')}
                  testId="login-submit-button"
                  disabled={isLoginSubmitButtonDisabled()}
                  submitted={onSubmit}
                ></LoginSubmitButton>
              </div>
            </form>
          )}
          {/* ChangePassword Progress */}
          {loginProgress === LoginProgress.ChangePassword && (
            <form noValidate onSubmit={changePasswordSubmit}>
              <p
                data-testid="change-password-title"
                className="noto-semibold-4 mb-[40px] leading-[24px] text-dark-4"
              >
                {t('login.changePassword')}
              </p>
              <LoginInput
                id="password"
                name="password"
                type="password"
                text={t('login.changePassword', { context: 'input' })}
                guidance={changePasswordValue.passwordGuidance}
                success={changePasswordValue.passwordSuccess}
                value={changePasswordValue.password}
                required
                setValue={(value) => {
                  setChangePasswordValue((prev) => ({
                    ...prev,
                    password: value,
                  }));
                  validatePassword(
                    value,
                    (message: string) =>
                      setChangePasswordValue((prev) => ({
                        ...prev,
                        passwordError: message,
                      })),
                    (message: string) =>
                      setChangePasswordValue((prev) => ({
                        ...prev,
                        passwordSuccess: message,
                      }))
                  );
                }}
                placeholder={t('login.changePassword', {
                  context: 'placeholder',
                })}
                className="noto-13-bold mb-[30px] text-dark-10"
                error={changePasswordValue.passwordError}
              ></LoginInput>
              <LoginInput
                id="passwordConfirm"
                name="passwordConfirm"
                type="password"
                value={changePasswordValue.passwordConfirm}
                text={t('login.confirmPassword', { context: 'input' })}
                setValue={(value) => {
                  setChangePasswordValue((prev) => ({
                    ...prev,
                    passwordConfirm: value,
                  }));
                  validatePassword(
                    value,
                    (message: string) =>
                      setChangePasswordValue((prev) => ({
                        ...prev,
                        passwordConfirmError: message,
                      })),
                    (message: string) =>
                      setChangePasswordValue((prev) => ({
                        ...prev,
                        passwordConfirmSuccess: message,
                      }))
                  );
                }}
                required
                placeholder={t('login.confirmPassword', {
                  context: 'placeholder',
                })}
                className="noto-13-bold text-dark-10"
                error={changePasswordValue.passwordConfirmError}
                success={changePasswordValue.passwordConfirmSuccess}
              ></LoginInput>
              <div className="mt-[24px] flex justify-end">
                <LoginSubmitButton
                  text={t('login.submit')}
                  disabled={isChangePasswordSubmitButtonDisabled()}
                  submitted={onSubmit}
                ></LoginSubmitButton>
              </div>
            </form>
          )}
          {/* FindPassword Progress */}
          {loginProgress === LoginProgress.FindPassword && (
            <form noValidate onSubmit={findPasswordSubmit}>
              <p
                data-testid="find-password-title"
                className="noto-semibold-4 leading-[24px] text-dark-4"
              >
                {t('login.findPassword', { context: 'title' })}
              </p>
              <p className="noto-13-normal mt-4 leading-5 text-dark-4">
                {t('login.findPassword', { context: 'desc1' })}
              </p>
              <p className="noto-13-normal mb-[34px] leading-5 text-dark-4">
                {t('login.findPassword', { context: 'desc2' })}
              </p>
              <LoginInput
                testId="find-password-email-input"
                id="email"
                name="email"
                type="email"
                text={t('login.email')}
                required
                value={findPasswordValue.email}
                setValue={(value) =>
                  setFindPasswordValue((prev) => ({ ...prev, email: value }))
                }
                placeholder="example@gmail.com"
                className="noto-13-bold mb-[20px] text-dark-10"
                error={findPasswordValue.emailError}
              ></LoginInput>
              <div className="mt-[24px] flex justify-end">
                <LoginSubmitButton
                  testId="find-password-submit"
                  text={t('login.submit')}
                  disabled={isFindPasswordSubmitButtonDisabled()}
                  submitted={onSubmit}
                ></LoginSubmitButton>
              </div>
            </form>
          )}
          {/* Confirm code Progress */}
          {loginProgress === LoginProgress.FindPasswordCodeSent && (
            <form noValidate onSubmit={codeSentSubmit}>
              <p
                data-testid="find-password-code-sent-title"
                className="noto-semibold-4 leading-[24px] text-dark-4"
              >
                {t('login.codeSent', { context: 'title' })}
              </p>
              <p className="noto-13-normal mt-4 leading-5 text-dark-4">
                {t('login.codeSent', { context: 'desc1' })}
              </p>
              <p className="noto-13-normal mb-[34px] leading-5 text-dark-4">
                {t('login.codeSent', { context: 'desc2' })}
              </p>
              <LoginInput
                testId="code-sent-confirm-code"
                id="confirmCode"
                name="confirmCode"
                type="text"
                text={t('login.confirmCode')}
                required
                value={findPasswordValue.confirmCode}
                setValue={(value) =>
                  setFindPasswordValue((prev) => ({
                    ...prev,
                    confirmCode: value,
                  }))
                }
                error={findPasswordValue.confirmCodeError}
                placeholder={t('login.confirmCode', { context: 'placeholder' })}
                className="noto-13-bold mb-[20px] text-dark-10"
              ></LoginInput>
              <LoginInput
                testId="code-sent-password"
                id="password"
                name="password"
                type="password"
                text={t('login.changePassword', { context: 'input' })}
                guidance={findPasswordValue.passwordGuidance}
                success={findPasswordValue.passwordSuccess}
                value={findPasswordValue.password}
                required
                setValue={(value) => {
                  setFindPasswordValue((prev) => ({
                    ...prev,
                    password: value,
                  }));
                  validatePassword(
                    value,
                    (message: string) =>
                      setFindPasswordValue((prev) => ({
                        ...prev,
                        passwordError: message,
                      })),
                    (message: string) =>
                      setFindPasswordValue((prev) => ({
                        ...prev,
                        passwordSuccess: message,
                      }))
                  );
                }}
                placeholder={t('login.changePassword', {
                  context: 'placeholder',
                })}
                className="noto-13-bold mb-[30px] text-dark-10"
                error={findPasswordValue.passwordError}
              ></LoginInput>
              <LoginInput
                testId="code-sent-password-confirm"
                id="passwordConfirm"
                name="passwordConfirm"
                type="password"
                value={findPasswordValue.passwordConfirm}
                text={t('login.confirmPassword', { context: 'input' })}
                setValue={(value) => {
                  setFindPasswordValue((prev) => ({
                    ...prev,
                    passwordConfirm: value,
                  }));
                  validatePassword(
                    value,
                    (message: string) =>
                      setFindPasswordValue((prev) => ({
                        ...prev,
                        passwordConfirmError: message,
                      })),
                    (message: string) =>
                      setFindPasswordValue((prev) => ({
                        ...prev,
                        passwordConfirmSuccess: message,
                      }))
                  );
                }}
                required
                placeholder={t('login.confirmPassword', {
                  context: 'placeholder',
                })}
                className="noto-13-bold text-dark-10"
                error={findPasswordValue.passwordConfirmError}
                success={findPasswordValue.passwordConfirmSuccess}
              ></LoginInput>
              <div className="mt-[24px] flex justify-end">
                <LoginSubmitButton
                  testId="code-sent-submit"
                  text={t('login.submit')}
                  disabled={isFindPasswordSentCodeSubmitButtonDisabled()}
                  submitted={onSubmit}
                ></LoginSubmitButton>
              </div>
            </form>
          )}
          {/* FindEmail Progress */}
          {loginProgress === LoginProgress.FindEmail && (
            <form noValidate onSubmit={findEmailSubmit}>
              <p
                data-testid="find-email-title"
                className="noto-semibold-4 mb-4 leading-[24px] text-dark-4"
              >
                아이디 찾기
              </p>
              <LoginInput
                testId="find-email-username-input"
                id="username"
                name="username"
                type="text"
                text="유저명"
                required
                value={findEmailValue.username}
                setValue={(value) =>
                  setFindEmailValue((prev) => ({ ...prev, username: value }))
                }
                placeholder="김아무개"
                className="noto-13-bold mb-[20px] text-dark-10"
                error={findEmailValue.usernameError}
              ></LoginInput>
              <LoginInput
                testId="find-email-contact-input"
                id="contact"
                name="contact"
                type="text"
                text={t('management.contactPerson')}
                required
                value={findEmailValue.contact}
                setValue={(value) =>
                  setFindEmailValue((prev) => ({ ...prev, contact: value }))
                }
                placeholder="010-9999-8888"
                className="noto-13-bold mb-[20px] text-dark-10"
                error={findEmailValue.contactError}
              ></LoginInput>
              <div className="mt-[24px] flex justify-end">
                <LoginSubmitButton
                  testId="find-email-submit"
                  text={t('login.submit')}
                  disabled={isFindEmailSubmitButtonDisabled()}
                  submitted={onSubmit}
                ></LoginSubmitButton>
              </div>
            </form>
          )}
          {/* FindEmail Progress */}
          {loginProgress === LoginProgress.FindEmailResult && (
            <form noValidate onSubmit={findEmailResultSubmit}>
              <p
                data-testid="find-email-result-title"
                className="noto-semibold-4 mb-4 leading-[24px] text-dark-4"
              >
                이메일은 다음과 같습니다.
              </p>
              <p
                data-testid="find-email-result-title"
                className="noto-semibold-4 mb-4 leading-[24px] text-dark-4"
              >
                {findEmailValue.emailResult}
              </p>
              <div className="mt-[24px] flex justify-end">
                <LoginSubmitButton
                  testId="find-email-result-submit"
                  text={t('login.submit')}
                  disabled={false}
                  submitted={onSubmit}
                ></LoginSubmitButton>
              </div>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}

export default LoginPage;
